/**
 * Camera distance helpers for PWA inspection.
 * Target: 15 cm ± 3 cm
 */

export const TARGET_DISTANCE_CM = 15;
export const DISTANCE_TOLERANCE_CM = 3;
export const ASSUMED_HFOV_DEG = 65;

/** Physical width of the on-screen guide at the target distance (fits FOV). */
export const ROI_WIDTH_MM = 160;
export const ROI_HEIGHT_MM = 160;

export const SQ_FT_MM2 = 304.8 * 304.8;

export function roiAreaSqFt(widthMm = ROI_WIDTH_MM, heightMm = ROI_HEIGHT_MM) {
  return (widthMm * heightMm) / SQ_FT_MM2;
}

export function estimateDistanceCm({
  guideWidthPx,
  imageWidthPx,
  guideSizeMm = ROI_WIDTH_MM,
  hfovDeg = ASSUMED_HFOV_DEG,
}) {
  if (!guideWidthPx || !imageWidthPx) return null;
  const hfovRad = (hfovDeg * Math.PI) / 180;
  const distanceMm =
    guideSizeMm /
    (2 * (guideWidthPx / imageWidthPx) * Math.tan(hfovRad / 2));
  const cm = distanceMm / 10;
  if (cm < 4 || cm > 60) return null;
  return cm;
}

export function guideFillRatioAtDistance(
  distanceCm = TARGET_DISTANCE_CM,
  guideSizeMm = ROI_WIDTH_MM,
  hfovDeg = ASSUMED_HFOV_DEG
) {
  const hfovRad = (hfovDeg * Math.PI) / 180;
  const sceneWidthMm = 2 * distanceCm * 10 * Math.tan(hfovRad / 2);
  return Math.min(0.92, guideSizeMm / sceneWidthMm);
}

/**
 * Smooth noisy distance readings (EMA).
 */
export function smoothDistance(prev, next, alpha = 0.28) {
  if (next == null || Number.isNaN(next)) return prev;
  if (prev == null || Number.isNaN(prev)) return next;
  return prev * (1 - alpha) + next * alpha;
}

export function classifyDistance(
  distanceCm,
  targetCm = TARGET_DISTANCE_CM,
  toleranceCm = DISTANCE_TOLERANCE_CM,
  { hasMarker = true } = {}
) {
  if (!hasMarker && (distanceCm == null || Number.isNaN(distanceCm))) {
    return {
      status: 'no_marker',
      direction: 'none',
      arrow: '',
      label: 'ยังไม่วัดระยะได้',
      hint: 'ใช้โหมดขาตั้ง หรือวางการ์ดอ้างอิง 5 ซม. ในกรอบ',
      canScan: false,
      deltaCm: null,
    };
  }

  if (distanceCm == null || Number.isNaN(distanceCm)) {
    return {
      status: 'unknown',
      direction: 'none',
      arrow: '',
      label: 'หาการ์ดอ้างอิงไม่เจอ',
      hint: 'วางสี่เหลี่ยมดำ 5×5 ซม. ให้เห็นชัดในกรอบ',
      canScan: false,
      deltaCm: null,
    };
  }

  const delta = distanceCm - targetCm;
  const abs = Math.abs(delta);

  if (abs <= toleranceCm) {
    return {
      status: 'ready',
      direction: 'ok',
      arrow: '✓',
      label: `ระยะถูกต้อง · ${distanceCm.toFixed(1)} ซม.`,
      hint: `อยู่ในช่วง ${targetCm} ± ${toleranceCm} ซม. · สแกนได้`,
      canScan: true,
      deltaCm: Number(delta.toFixed(1)),
    };
  }

  if (delta < 0) {
    // too close → move phone away
    return {
      status: 'too_close',
      direction: 'away',
      arrow: '↑',
      label: `ถอยออก · ตอนนี้ ${distanceCm.toFixed(1)} ซม.`,
      hint: `ใกล้เกิน ${abs.toFixed(1)} ซม. · เป้า ${targetCm} ± ${toleranceCm} ซม.`,
      canScan: false,
      deltaCm: Number(delta.toFixed(1)),
    };
  }

  return {
    status: 'too_far',
    direction: 'closer',
    arrow: '↓',
    label: `เข้าใกล้ · ตอนนี้ ${distanceCm.toFixed(1)} ซม.`,
    hint: `ไกลเกิน ${abs.toFixed(1)} ซม. · เป้า ${targetCm} ± ${toleranceCm} ซม.`,
    canScan: false,
    deltaCm: Number(delta.toFixed(1)),
  };
}

export function mmPerPixelAtDistance(
  distanceCm,
  imageWidthPx,
  hfovDeg = ASSUMED_HFOV_DEG
) {
  const hfovRad = (hfovDeg * Math.PI) / 180;
  const sceneWidthMm = 2 * distanceCm * 10 * Math.tan(hfovRad / 2);
  return sceneWidthMm / imageWidthPx;
}
